import React, { useEffect, useState } from 'react';
import { ClipAPI } from '../../api/api';
import { constructMediaUrl } from '../../api/helpers';
import { ReviewLogEntry, ReviewVariant } from '../../api/structs/media';
import { ReviewVerdict } from '../../api/structs/pipeline';
import { failedFindings } from '../ideas/pipeline-run/CheckpointVerdictPanel';
import ReviewFindingsList from '../ideas/pipeline-run/ReviewFindingsList';
import MediaThumbImage from '../clips/MediaThumbImage';

/**
 * The clip-side reviewer's read on ONE scene item: a badge derived from
 * `metadata.review_verdict`, expanding into the findings that produced it,
 * plus the trim it proposed, the re-render variants it produced, and its
 * audit log. Renders nothing when the item was never reviewed.
 *
 * A presentational component only — both mount sites (the editor's
 * `SlideInspector` and the clips view's `MediaEditorSceneDetailPanel`) own
 * the actual persistence: `onUndoTrim` clears `trim_start`/`trim_end`
 * through whichever media-metadata update that surface already uses, and
 * `onRefresh` re-fetches the clip after a variant swap.
 */

export interface ReviewerPanelProps {
  clipId: string;
  /** The canonical scene item's id — what `select-variant` swaps the file of. */
  mediaId: string;
  /** The scene item's own metadata (review_verdict/review_variants/... live here). */
  metadata: Record<string, any>;
  /**
   * Called with the pre-trim duration (in seconds) when it is known, so the
   * caller can restore `metadata.duration` alongside the trim keys — a
   * declared duration beats the trim range everywhere it is read, so clearing
   * only `trim_start`/`trim_end` would leave the slide at the trimmed length.
   * Called with `undefined` when no before-value can be derived, so the
   * caller should clear `duration` outright rather than guess at it.
   */
  onUndoTrim: (beforeDuration?: number) => void | Promise<void>;
  onRefresh: () => void;
  /** Distinguishes ids when several panels render on one page. */
  idPrefix?: string;
}

const badgeLabel = (verdict: ReviewVerdict): string => {
  if (verdict.pass) return '✓ reviewed';
  return `⚠ ${failedFindings(verdict).length || 1} failed`;
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const round1 = (value: number): number => Math.round(value * 10) / 10;

/** "pass" / "N fail(s)" / "not judged" — a variant whose fail_count is the
 * unknown-judgement sentinel (-1) was never judged (frame sampling or the
 * judge call failed), which is not the same thing as a clean pass. */
const variantOutcomeText = (variant: ReviewVariant): string => {
  if (variant.fail_count === -1) return 'not judged';
  return variant.pass ? 'pass' : `${variant.fail_count} fail(s)`;
};

/** Same `/media/thumb/<id>.webp` derivative the media library grid uses
 * (contentgen-go-backend media/serveThumbnail.go) — may 404, which is what
 * `MediaThumbImage` falls back to `videoUrl` for. */
const variantThumbUrl = (mediaId: string): string => constructMediaUrl(`/media/thumb/${mediaId}.webp`);

/** True when the verdict carries an applied change worth surfacing unprompted:
 * a proposed trim or at least one re-render variant. */
const reviewerMadeChanges = (metadata: Record<string, any>): boolean => {
  const verdict = metadata?.review_verdict;
  if (!verdict) return false;
  const trims = Boolean(toNumber(verdict.metadata?.trim_start) || toNumber(verdict.metadata?.trim_end));
  const variants = Array.isArray(metadata?.review_variants) ? metadata.review_variants : [];
  return trims || variants.length > 0;
};

const ReviewerPanel: React.FC<ReviewerPanelProps> = ({
  clipId,
  mediaId,
  metadata,
  onUndoTrim,
  onRefresh,
  idPrefix = 'clip-review',
}) => {
  // Open by default when the reviewer actually DID something (a trim or a
  // re-render): the user's question then is "what changed?", and hiding the
  // answer behind a 10px badge is how it goes unseen. A plain pass/fail read
  // with no changes stays collapsed.
  const [open, setOpen] = useState(() => reviewerMadeChanges(metadata));
  const [selecting, setSelecting] = useState<string | null>(null);
  // Optimistic override for `selected_variant`, applied the instant a variant
  // is clicked so the marker moves before the request settles. `undefined`
  // means "defer to the server value in `metadata`".
  const [localSelectedVariant, setLocalSelectedVariant] = useState<string | undefined>(undefined);
  const [variantError, setVariantError] = useState<string | null>(null);

  // Neither mount site remounts this component on every media-item swap: a
  // regenerate/replace hands back a new item under the SAME timeline block
  // (same `key`), so `mediaId` can change without SlideInspector/
  // MediaEditorSceneDetailPanel ever tearing down. Without this, a stale
  // optimistic selection or error from the PREVIOUS item would linger over
  // the new one's real `selected_variant`.
  useEffect(() => {
    setSelecting(null);
    setLocalSelectedVariant(undefined);
    setVariantError(null);
    setOpen(reviewerMadeChanges(metadata));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);

  const verdict = (metadata?.review_verdict as ReviewVerdict | undefined) || null;
  if (!verdict) return null;

  const findings = verdict.findings || [];
  const variants = (metadata?.review_variants as ReviewVariant[] | undefined) || [];
  const serverSelectedVariant = metadata?.selected_variant as string | undefined;
  const selectedVariant = localSelectedVariant ?? serverSelectedVariant;
  const log = (metadata?.review_log as ReviewLogEntry[] | undefined) || [];

  // The verdict's own metadata channel carries what the AI proposed to trim,
  // in seconds off each end — independent of whether it is still applied
  // (undoing it clears the media item's trim_start/trim_end, not this record).
  const trimHead = toNumber(verdict.metadata?.trim_start);
  const trimTail = toNumber(verdict.metadata?.trim_end);
  const hasTrim = Boolean(trimHead || trimTail);

  // After-duration precedence: the item's own APPLIED trim (the absolute
  // source-time keys the render/editor timeline actually uses) wins when both
  // ends are set; metadata.duration (the declared on-screen duration) is the
  // fallback for items that only ever got a duration written, never trim keys.
  const appliedTrimStart = toNumber(metadata?.trim_start);
  const appliedTrimEnd = toNumber(metadata?.trim_end);
  const afterDuration =
    appliedTrimStart !== undefined && appliedTrimEnd !== undefined
      ? appliedTrimEnd - appliedTrimStart
      : toNumber(metadata?.duration);
  const beforeDuration =
    afterDuration !== undefined ? afterDuration + (trimHead || 0) + (trimTail || 0) : undefined;

  const panelId = `${idPrefix}-panel-${mediaId}`;

  const handleSelectVariant = async (variant: ReviewVariant) => {
    if (selecting) return;
    const previousSelected = selectedVariant;
    setVariantError(null);
    setSelecting(variant.media_id);
    // Optimistic: mark this variant selected immediately, before the request
    // settles — reverted below if the server rejects it.
    setLocalSelectedVariant(variant.media_id);
    try {
      const response = await ClipAPI.selectVariant(clipId, mediaId, variant.media_id);
      setLocalSelectedVariant(response?.selected_variant || variant.media_id);
      onRefresh();
    } catch (error: any) {
      setLocalSelectedVariant(previousSelected);
      setVariantError('Could not select variant');
    } finally {
      setSelecting(null);
    }
  };

  return (
    <div className="space-y-1.5" data-testid={`${idPrefix}-root`}>
      <button
        type="button"
        data-testid="clip-review-badge"
        data-state={verdict.pass ? 'pass' : 'fail'}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
        className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] uppercase tracking-wide transition-colors ${
          verdict.pass
            ? 'border-white/15 text-zinc-400 hover:text-zinc-200'
            : 'border-amber-400/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20'
        }`}
      >
        {badgeLabel(verdict)}
      </button>

      {open && (
        <div id={panelId} data-testid="clip-review-panel" className="space-y-2 border border-white/10 bg-black/50 p-2">
          <ReviewFindingsList findings={findings} />

          {verdict.critique && (
            <p className="text-[11px] leading-relaxed text-zinc-300 whitespace-pre-wrap">{verdict.critique}</p>
          )}

          {hasTrim && (
            <div className="space-y-1 border-t border-white/10 pt-2" data-testid="clip-review-trim">
              <p className="text-[11px] text-zinc-300">
                AI trimmed {trimHead ?? 0}s head, {trimTail ?? 0}s tail
              </p>
              {beforeDuration !== undefined && afterDuration !== undefined && (
                <p className="text-[10px] text-zinc-500">
                  {round1(beforeDuration)}s → {round1(afterDuration)}s
                </p>
              )}
              <button
                type="button"
                data-testid="clip-review-undo-trim"
                className="btn btn-sm btn-secondary text-[10px]"
                onClick={() => void onUndoTrim(beforeDuration)}
              >
                Undo trim
              </button>
            </div>
          )}

          {variants.length > 0 && (
            <div className="space-y-1 border-t border-white/10 pt-2" data-testid="clip-review-variants">
              <p className="text-[10px] uppercase tracking-wide text-zinc-500">Variants</p>
              <div className="flex flex-wrap gap-1.5">
                {variants.map((variant) => {
                  const isSelected = variant.media_id === selectedVariant;
                  const label = variant.source === 'original' ? 'original' : `re-render #${variant.attempt}`;
                  return (
                    <button
                      key={variant.media_id}
                      type="button"
                      data-testid={`clip-review-variant-${variant.media_id}`}
                      aria-pressed={isSelected}
                      disabled={selecting === variant.media_id}
                      onClick={() => void handleSelectVariant(variant)}
                      title={`${label} · ${variantOutcomeText(variant)}`}
                      className={`w-20 border p-1 text-left text-[9px] ${
                        isSelected ? 'border-emerald-400 text-emerald-300' : 'border-white/15 text-zinc-300'
                      }`}
                    >
                      <MediaThumbImage
                        thumbUrl={variantThumbUrl(variant.media_id)}
                        videoUrl={constructMediaUrl(variant.file_url)}
                        alt={label}
                        className="mb-1 block h-10 w-full rounded-sm bg-zinc-800"
                        imgClassName="h-10 w-full rounded-sm object-cover"
                      />
                      <span className="block truncate uppercase tracking-wide">{label}</span>
                      <span
                        className={
                          variant.fail_count === -1
                            ? 'text-zinc-400'
                            : variant.pass
                              ? 'text-emerald-300'
                              : 'text-amber-300'
                        }
                      >
                        {variantOutcomeText(variant)}
                      </span>
                      {isSelected && <span className="block text-white">selected</span>}
                    </button>
                  );
                })}
              </div>
              {variantError && (
                <p role="alert" data-testid="clip-review-variant-error" className="text-[10px] text-red-300">
                  {variantError}
                </p>
              )}
            </div>
          )}

          {log.length > 0 && (
            <div className="space-y-1 border-t border-white/10 pt-2" data-testid="clip-review-log">
              <p className="text-[10px] uppercase tracking-wide text-zinc-500">Log</p>
              <ul className="space-y-0.5">
                {log.map((entry, index) => (
                  <li
                    key={`${entry.ts}-${index}`}
                    data-testid="clip-review-log-entry"
                    className="text-[10px] text-zinc-400"
                  >
                    <span className="text-zinc-600">{new Date(entry.ts).toLocaleTimeString()}</span>{' '}
                    <span className="uppercase text-zinc-300">{entry.action}</span> {entry.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewerPanel;
